import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import { ActiveFilterConfig } from '@src/app/shared/interfaces/active-filter.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import { DrawerModule } from 'primeng/drawer';

/** Filtros da lista de usuários que ficam no painel (empresa e busca ficam na página). */
export interface UserListFilters {
  status: 'all' | 'active' | 'inactive';
  password: '' | 'set' | 'pending' | 'link-expired';
  role: string;
}

export const DEFAULT_USER_LIST_FILTERS: UserListFilters = { status: 'all', password: '', role: '' };

export const USER_STATUS_OPTIONS: Option[] = [
  { label: 'Ativos e inativos', value: 'all' },
  { label: 'Só ativos', value: 'active' },
  { label: 'Só inativos', value: 'inactive' },
];

export const USER_PASSWORD_OPTIONS: Option[] = [
  { label: 'Qualquer situação', value: '' },
  { label: 'Senha definida', value: 'set' },
  { label: 'Sem senha', value: 'pending' },
  { label: 'Link de senha vencido', value: 'link-expired' },
];

export const USER_ROLE_OPTIONS: Option[] = [
  { label: 'Todos os perfis', value: '' },
  { label: 'Admin', value: 'ADMIN' },
  { label: 'Tenant', value: 'TENANT' },
  { label: 'Empresa', value: 'COMPANY' },
  { label: 'Parceiro', value: 'PARTNER' },
];

/** Chips dos filtros aplicados (só os que fogem do padrão), para o `app-active-filters`. */
export function buildUserActiveFilters(filters: UserListFilters): ActiveFilterConfig[] {
  const label = (options: Option[], value: string) =>
    options.find(option => option.value === value)?.label ?? value;
  const chips: ActiveFilterConfig[] = [];
  if (filters.status !== 'all') {
    chips.push({
      propertyName: 'status',
      label: 'Status',
      type: 'text',
      value: label(USER_STATUS_OPTIONS, filters.status),
      group: 'Status',
    });
  }
  if (filters.password) {
    chips.push({
      propertyName: 'password',
      label: 'Senha',
      type: 'text',
      value: label(USER_PASSWORD_OPTIONS, filters.password),
      group: 'Senha',
    });
  }
  if (filters.role) {
    chips.push({
      propertyName: 'role',
      label: 'Perfil',
      type: 'text',
      value: label(USER_ROLE_OPTIONS, filters.role),
      group: 'Perfil',
    });
  }
  return chips;
}

/**
 * Painel de filtros da lista de usuários (card 3697), no padrão do filtro da Gestão de Empréstimos: drawer à
 * direita, aberto pelo funil da tabela. Status, situação da senha e perfil; aplica ao clicar em "Aplicar".
 */
@Component({
  selector: 'app-user-filter',
  templateUrl: './user-filter.component.html',
  styleUrls: ['./user-filter.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, DrawerModule],
})
export class UserFilterComponent {
  /** Abre e fecha o painel. */
  @Input() set isVisible(value: boolean) {
    this.drawerVisible = value;
    if (value) this.draft = { ...this.current };
  }
  /** Filtros aplicados agora (o painel abre com eles). */
  @Input() current: UserListFilters = { ...DEFAULT_USER_LIST_FILTERS };
  @Output() sendCloseFilter = new EventEmitter<void>();
  @Output() sendFilter = new EventEmitter<UserListFilters>();

  readonly statusOptions = USER_STATUS_OPTIONS;
  readonly passwordOptions = USER_PASSWORD_OPTIONS;
  readonly roleOptions = USER_ROLE_OPTIONS;

  drawerVisible = false;
  /** Rascunho do painel: só vale ao aplicar. */
  draft: UserListFilters = { ...DEFAULT_USER_LIST_FILTERS };

  /** Aplica o rascunho e fecha. */
  apply(): void {
    this.sendFilter.emit({ ...this.draft });
    this.close();
  }

  /** Volta o rascunho ao padrão e aplica. */
  clear(): void {
    this.draft = { ...DEFAULT_USER_LIST_FILTERS };
    this.apply();
  }

  /** Fecha sem aplicar. */
  close(): void {
    this.drawerVisible = false;
    this.sendCloseFilter.emit();
  }
}
